// Header.js - Top header component
import React from 'react';
import styled from 'styled-components';
import { RichCoinIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import dogeCoinImage from '../assets/images/dogecoin-logo.svg';

// ----------------------------
// CONFIGURATION
// ----------------------------

const HEADER_ICONS = {
  add: {
    id: 'add',
    name: 'Add',
    icon: 'material-symbols:add',
    color: '#ffffff',
    size: '18px'
  },
  settings: {
    id: 'settings',
    name: 'Settings',
    icon: 'uil:setting',
    color: '#9E9E9E',
    size: '22px'
  },
  wallet: {
    id: 'wallet',
    name: 'Wallet',
    icon: 'iconoir:wallet-solid',
    color: '#000000',
    size: '22px'
  }
};

// Header Container & Sections
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding: clamp(8px, 2vh, 16px) clamp(12px, 3vw, 20px);
  background-color: #ffffff;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  position: fixed;
  top: 19px;
  left: 0;
  right: 0;
  z-index: 1000;
  min-height: clamp(44px, 10vh, 60px);
  margin: 0;
  border: none;
  box-sizing: border-box;
  
  /* Responsive breakpoints */
  @media (max-width: 375px) {
    padding: clamp(6px, 1.5vh, 12px) clamp(10px, 2.5vw, 16px);
    min-height: clamp(40px, 8vh, 50px);
  }
  
  @media (min-width: 768px) {
    padding: clamp(12px, 2.5vh, 20px) clamp(16px, 4vw, 24px);
    min-height: clamp(50px, 12vh, 70px);
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  width: 20px;
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 0 auto;
  justify-content: center;
  flex: 1;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 20px;
`;

// Header Elements
const AppTitle = styled.div`
  font-size: clamp(14px, 4vw, 18px);
  font-weight: bold;
  margin-left: clamp(4px, 1vw, 8px);
  
  @media (max-width: 375px) {
    font-size: clamp(12px, 3.5vw, 16px);
  }
  
  @media (min-width: 768px) {
    font-size: clamp(16px, 3vw, 20px);
  }
`;

const VerifiedIconWrapper = styled.div`
  transform: scale(0.8) translateY(2px);
`;

// Button Styling
const ButtonIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(4px, 1vh, 8px);
  
  &.with-margin {
    margin-left: clamp(6px, 1.5vw, 12px);
  }
  
  svg {
    width: clamp(20px, 5.5vw, 26px);
    height: clamp(20px, 5.5vw, 26px);
  }
  
  /* Responsive icon sizes */
  @media (max-width: 375px) {
    padding: clamp(3px, 0.8vh, 6px);
    
    svg {
      width: clamp(18px, 5vw, 22px);
      height: clamp(18px, 5vw, 22px);
    }
  }
  
  @media (min-width: 768px) {
    padding: clamp(6px, 1.2vh, 10px);
    
    svg {
      width: clamp(22px, 4vw, 28px);
      height: clamp(22px, 4vw, 28px);
    }
  }
`;

// Coin Info Section
const CoinInfoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 10px;
  background-color: white;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 100%;
  position: sticky;
  top: 0;
  z-index: 99;
  flex-shrink: 0;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const CoinSection = styled.div`
  display: flex;
  align-items: center;
  padding: 3px 6px;
  gap: 3px;
  background-color: ${props => props.$background || 'transparent'};
  border-radius: 12px;
  
  &.auto-margin {
    margin-left: auto;
  }
`;

const CoinValue = styled.div`
  font-weight: bold;
  font-size: 13px;
`;

const AddButton = styled.button`
  background-color: black;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  max-width: 28px;
  max-height: 28px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
`;

const DogeImage = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 50%;
`;

// Using React.memo to prevent unnecessary re-renders
const Header = React.memo(({ dogeBalance, pxlBalance }) => {
  const handleAddClick = () => {
    // Add your add functionality here
    console.log('Add button clicked');
  };
  
  const handleSettingsClick = () => {
    // Add your settings functionality here
    console.log('Settings button clicked');
  };
  
  const handleWalletClick = () => {
    // Add your wallet functionality here
    console.log('Wallet button clicked');
  };
  
  return (
    <>
      <HeaderContainer>
        <LeftSection>
          {/* Empty space for balance */}
        </LeftSection>
        
        <CenterSection>
          <RichCoinIcon width="30" height="30" />
          <AppTitle>PXL App</AppTitle>
          <VerifiedIconWrapper>
            <Icon icon="material-symbols:verified" width="24" height="24" color="#1DA1F2" />
          </VerifiedIconWrapper>
        </CenterSection>                                    
        
        <RightSection>
          {/* Empty space for balance */}
        </RightSection>
      </HeaderContainer>

      <CoinInfoContainer>
        <CoinSection>
          <RichCoinIcon width="35" height="35" />
          <CoinValue>{pxlBalance ? pxlBalance.toFixed(0) : '0'}</CoinValue>
          <AddButton onClick={handleAddClick}>
            <Icon 
              icon={HEADER_ICONS.add.icon}
              color={HEADER_ICONS.add.color}
              width={HEADER_ICONS.add.size}
              height={HEADER_ICONS.add.size}
            />
          </AddButton>        </CoinSection>
          <CoinSection $background="#f5f5f5" className="auto-margin">
          <DogeImage src={dogeCoinImage} alt="Doge" />
          <CoinValue>{dogeBalance ? dogeBalance.toFixed(2) : '0.00'}</CoinValue>
        </CoinSection>
        
        <ButtonIcon className="with-margin" onClick={handleSettingsClick}>
          <Icon 
            icon={HEADER_ICONS.settings.icon}
            color={HEADER_ICONS.settings.color}
            width={HEADER_ICONS.settings.size}
            height={HEADER_ICONS.settings.size}
          />
        </ButtonIcon>
        
        <ButtonIcon onClick={handleWalletClick}>
          <Icon 
            icon={HEADER_ICONS.wallet.icon}
            color={HEADER_ICONS.wallet.color}
            width={HEADER_ICONS.wallet.size}
            height={HEADER_ICONS.wallet.size}
          />
        </ButtonIcon>
      </CoinInfoContainer>
    </>
  );
});

export default Header;
